import { useState, type FC } from 'react'

import { Checkbox, Input, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'

import terminationResources from './resources'

type Props = {
  orderId: number
  email: string
  isOpen: boolean
  onClose: () => void
  onSuccess: (replaceEmail: boolean) => void
}

export const ResendFormModal: FC<Props> = ({ orderId, email, isOpen, onClose, onSuccess }) => {
  const [formEmail, setFormEmail] = useState(email)
  const [replaceEmail, setReplaceEmail] = useState(false)

  const mutation = useMutation(
    (payload: { email: string; replace_email: boolean }) =>
      terminationResources.resendForm(orderId, payload),
    {
      onSuccess: (_data, variables) => {
        onCloseHandler()
        onSuccess(variables.replace_email)
      },
    }
  )

  const onCloseHandler = () => {
    setFormEmail(email)
    setReplaceEmail(false)
    onClose()
  }

  return (
    <MessagePopup
      title={<Translation id="Resend form to user" />}
      message=""
      isOpen={isOpen}
      onClose={onCloseHandler}
    >
      <div className="flex w-full flex-col gap-4">
        <Input
          isFullWidth
          id="resend-email"
          label={<Translation id="Email" />}
          value={formEmail}
          onChange={e => setFormEmail(e.target.value)}
        />

        <Checkbox
          id="replace-email"
          label={<Translation id="Replace email" />}
          checked={replaceEmail}
          onChange={() => setReplaceEmail(prev => !prev)}
        />
      </div>

      <footer className="mt-4 flex justify-end gap-2">
        <Button variant="tertiary" onClick={onCloseHandler}>
          <Translation id="Cancel" />
        </Button>

        <Button
          variant="primary"
          isLoading={mutation.isLoading}
          onClick={() => mutation.mutate({ email: formEmail, replace_email: replaceEmail })}
          disabled={!formEmail}
        >
          <Translation id="Send" />
        </Button>
      </footer>
    </MessagePopup>
  )
}
